import React from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  X, Info, Search, Building2, Calendar, Wallet, Bell, Users, 
  MessageSquare, BarChart3, CheckCircle, Globe, Briefcase, 
  Scissors, Heart, GraduationCap, Home, Music, Wrench, 
  Lock, HelpCircle, Settings, Plus
} from 'lucide-react';
import AnimatedLink from './AnimatedLink';

interface LearnAboutAACoreModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LearnAboutAACoreModal: React.FC<LearnAboutAACoreModalProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 1030 }}>
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full my-8 max-h-[90vh] flex flex-col">
        {/* Modal Header */}
        <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 relative">
          {/* Title and Close Button Row */}
          <div className="flex items-center justify-between mb-4 md:mb-0">
            <div className="flex items-center gap-2">
              <Info className="h-5 w-5 text-blue-900" strokeWidth={2} />
              <span className="text-lg font-semibold text-gray-900">Learn about AACore Booking</span>
            </div>
            <div className="flex items-center gap-3">
              {/* Action Buttons Row */}
              <div className="hidden md:flex items-center gap-3">
                <button
                  onClick={() => {
                    onClose();
                    navigate('/search');
                  }}
                  className="aacore-btn-outlined"
                >
                  <Search className="h-4 w-4" />
                  Find a Business
                </button>
                <AnimatedLink
                  to="/register"
                  onClick={onClose}
                  className="aacore-btn-primary"
                >
                  <Plus className="h-4 w-4" />
                  Create Account
                </AnimatedLink>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 flex-shrink-0"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          {/* Action Buttons Row - Mobile */}
          <div className="flex md:hidden items-stretch gap-3 mt-4">
            <button
              onClick={() => {
                onClose();
                navigate('/search');
              }}
              className="aacore-btn-outlined flex-1"
            >
              <Search className="h-4 w-4" />
              Find a Business
            </button>
            <AnimatedLink
              to="/register"
              onClick={onClose}
              className="aacore-btn-primary flex-1"
            >
              <Plus className="h-4 w-4" />
              Create Account
            </AnimatedLink>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-8 space-y-12">
          {/* Main Title */}
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Everything you need to book or run appointments
            </h2>

            {/* Feature Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Smart Scheduling */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mb-4">
                  <Calendar className="h-6 w-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Smart Scheduling</h3>
                <p className="text-sm text-gray-600">
                  Automated booking system that manages your calendar and prevents double bookings.
                </p>
              </div>

              {/* Payments & Invoices */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-4">
                  <Wallet className="h-6 w-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Payments & Invoices</h3>
                <p className="text-sm text-gray-600">
                  Accept payments online and generate invoices automatically for your services.
                </p>
              </div>

              {/* Reminders that Work */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-4">
                  <Bell className="h-6 w-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Reminders that Work</h3>
                <p className="text-sm text-gray-600">
                  Automated SMS and email reminders to reduce no-shows and keep customers informed.
                </p>
              </div>

              {/* Customer Profiles */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-red-500 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Customer Profiles</h3>
                <p className="text-sm text-gray-600">
                  Comprehensive customer database with booking history and preferences.
                </p>
              </div>

              {/* Messaging & Reviews */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mb-4">
                  <MessageSquare className="h-6 w-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Messaging & Reviews</h3>
                <p className="text-sm text-gray-600">
                  Communicate with customers and collect reviews to build your reputation.
                </p>
              </div>

              {/* Insights */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="h-6 w-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Insights</h3>
                <p className="text-sm text-gray-600">
                  Analytics and reports to understand your business performance and trends.
                </p>
              </div>
            </div>
          </div>

          {/* How It Works Section */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-4 w-4 text-gray-400" strokeWidth={2} />
              <span className="text-xs uppercase text-gray-500 tracking-wide">HOW IT WORKS</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Three simple steps</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Step 1 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Create your free site</h3>
                    <p className="text-sm text-gray-600">
                      Sign up and get a branded booking page instantly, no code and no hosting.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Add services & availability</h3>
                    <p className="text-sm text-gray-600">
                      Define services, durations, staff, and hours. Sync with your calendar.
                    </p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Start taking bookings</h3>
                    <p className="text-sm text-gray-600">
                      Share your link anywhere. Get paid, get reminders, and grow repeat customers.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Free Business Website */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start gap-4">
                <Globe className="h-6 w-6 text-blue-900 flex-shrink-0 mt-1" strokeWidth={2} />
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    Free Business Website: Every business and freelancer gets a public URL like
                  </h3>
                  <div className="mb-2 break-words">
                    <span className="bg-black text-white px-2 py-1 rounded font-mono text-sm break-all inline-block">
                      https://booking.aacore.net/&#123;your_business&#125;
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Point your domain to it or share the link on social, Google, and email.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Who It's For Section */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Briefcase className="h-4 w-4 text-gray-400" strokeWidth={2} />
              <span className="text-xs uppercase text-gray-500 tracking-wide">WHO IT'S FOR</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">Built for all service businesses</h2>

            {/* Service Categories Grid */}
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 mb-8">
              {[
                { name: 'Beauty & Barber', icon: Scissors, color: 'blue' },
                { name: 'Fitness & Coaching', icon: Heart, color: 'green' },
                { name: 'Tutors & Classes', icon: GraduationCap, color: 'orange' },
                { name: 'Consulting & Legal', icon: Briefcase, color: 'red' },
                { name: 'Clinics & Wellness', icon: Building2, color: 'blue' },
                { name: 'Home Services', icon: Home, color: 'green' },
                { name: 'Creative & Events', icon: Music, color: 'orange' },
                { name: 'Freelancers & More', icon: Wrench, color: 'red' },
              ].map((service, index) => {
                const Icon = service.icon;
                const colorClasses = {
                  blue: 'text-blue-900',
                  green: 'text-green-600',
                  orange: 'text-orange-600',
                  red: 'text-red-600',
                };
                return (
                  <div key={index} className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm text-center">
                    <Icon className={`h-8 w-8 mx-auto mb-2 ${colorClasses[service.color as keyof typeof colorClasses]}`} strokeWidth={2} />
                    <p className="text-sm font-medium text-gray-900">{service.name}</p>
                  </div>
                );
              })}
            </div>

            {/* Platform Features */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Secure & Private */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-blue-900 rounded-lg flex items-center justify-center mb-4">
                  <Lock className="h-6 w-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Secure & Private</h3>
                <p className="text-sm text-gray-600">
                  Data hosted in the U.S. with modern encryption and role-based access controls.
                </p>
              </div>

              {/* Friendly Support */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center mb-4">
                  <HelpCircle className="h-6 w-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Friendly Support</h3>
                <p className="text-sm text-gray-600">
                  We're here to help from setup to migration and best-practice guidance.
                </p>
              </div>

              {/* Flexible by Design */}
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center mb-4">
                  <Settings className="h-6 w-6 text-white" strokeWidth={2} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Flexible by Design</h3>
                <p className="text-sm text-gray-600">
                  Works for single providers or multi-location teams. Configure what you need, nothing you don't.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearnAboutAACoreModal;

